import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';

export interface ToastProps {
  message: string;
  type?: 'success' | 'error' | 'info' | 'warning';
  duration?: number;
  onClose?: () => void;
}

export const Toast: React.FC<ToastProps> = ({
  message,
  type = 'info',
  duration = 3000,
  onClose,
}) => {
  const [mounted, setMounted] = useState(false);
  const [animating, setAnimating] = useState(false);

  useEffect(() => {
    if (!message) return;
    setMounted(true);
    const animTimer = setTimeout(() => setAnimating(true), 20);

    const autoCloseTimer = setTimeout(() => {
      handleClose();
    }, duration);

    return () => {
      clearTimeout(animTimer);
      clearTimeout(autoCloseTimer);
    };
  }, [message, duration]);

  const handleClose = () => {
    setAnimating(false);
    setTimeout(() => {
      setMounted(false);
      onClose?.();
    }, 300);
  };

  if (!mounted || !message) return null;

  const styleMap = {
    success: {
      border: 'border-emerald-200/90',
      shadow: 'shadow-emerald-500/10',
      icon: 'ri-checkbox-circle-fill text-emerald-500',
    },
    error: {
      border: 'border-rose-200/90',
      shadow: 'shadow-rose-500/10',
      icon: 'ri-error-warning-fill text-rose-500',
    },
    info: {
      border: 'border-sky-200/90',
      shadow: 'shadow-sky-500/10',
      icon: 'ri-information-fill text-sky-500',
    },
    warning: {
      border: 'border-amber-200/90',
      shadow: 'shadow-amber-500/10',
      icon: 'ri-alert-fill text-amber-500',
    },
  };

  const styleConfig = styleMap[type] || styleMap.info;

  const toastContent = (
    <div
      style={{ zIndex: 999999 }}
      className={`fixed top-8 left-1/2 -translate-x-1/2 flex items-center gap-3 px-4 py-3 rounded-2xl bg-white/95 border ${styleConfig.border} text-foreground-950 text-xs font-semibold shadow-xl ${styleConfig.shadow} backdrop-blur-xl transition-all duration-300 ease-out pointer-events-auto ${
        animating
          ? 'opacity-100 translate-y-0 scale-100'
          : 'opacity-0 -translate-y-4 scale-95'
      }`}
    >
      <i className={`${styleConfig.icon} text-lg shrink-0`}></i>
      <span className="whitespace-nowrap tracking-wide">{message}</span>
      <button
        type="button"
        onClick={handleClose}
        className="ml-1 p-0.5 text-foreground-400 hover:text-foreground-700 hover:bg-gray-100 rounded-full transition-colors cursor-pointer"
      >
        <i className="ri-close-line text-sm"></i>
      </button>
    </div>
  );

  return ReactDOM.createPortal(toastContent, document.body);
};

export default Toast;
